import React, { useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Switch } from '../../components/ui/Switch/Switch';
import { Tabs, type TabItem } from '../../components/ui/Tabs/Tabs';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { TableFooter } from '../../components/ui/TableFooter/TableFooter';
import { Tooltip } from '../../components/ui/Tooltip/Tooltip';
import { FilterBar, FilterSearch } from '../../components/ui/FilterBar/FilterBar';
import { DataTable } from '../../components/ui/DataTable/DataTable';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { IconDownload, IconSpinner } from '../../assets/icons/icons';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    useGetCleanupStatusQuery,
    useGetStorageListQuery,
    useToggleCleanupStatusMutation,
} from '../../entities/storage/api/storageApi';
import type { StorageItem, StorageScope } from '../../entities/storage/model/types';
import { downloadScreenshots } from '../../entities/project/api/screenshots';
import { DownloadError } from '../../shared/api/download';
import { DEFAULT_PAGE_SIZE } from '../../shared/config/pagination';
import { formatRangeLabel } from '../../utils/format';
import { formatNumber } from '../../utils/number';
import { cx } from '../../utils/cx';
import styles from './StoragePage.module.scss';

const TABS: TabItem<StorageScope>[] = [
    { key: 'users', label: 'Пользователи' },
    { key: 'organizations', label: 'Организации' },
];

const CONFIRM_SIZE_MB = 250;

function formatSize(megabytes: number): string {
    if (!megabytes) return '—';
    if (megabytes >= 1024) return `${(megabytes / 1024).toFixed(2).replace('.', ',')} ГБ`;
    return `${megabytes.toFixed(2).replace('.', ',')} МБ`;
}

const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export function StoragePage() {
    const toast = useToast();
    const [scope, setScope] = useState<StorageScope>('users');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const query = useDebouncedValue(search, 400);

    const { data: cleanupEnabled = false, isLoading: isStatusLoading, isError: isStatusError } =
        useGetCleanupStatusQuery();
    const [toggleCleanup, { isLoading: isToggling }] = useToggleCleanupStatusMutation();

    const { data, isLoading, isFetching, isError, refetch } = useGetStorageListQuery({
        scope,
        pageNum: page,
        pageSize: DEFAULT_PAGE_SIZE,
        query,
    });

    const items = data?.items ?? [];
    const pageCount = Math.max(1, data?.pages ?? 1);
    const total = data?.total ?? 0;

    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [unavailable, setUnavailable] = useState<Set<string>>(new Set());
    const [confirmItem, setConfirmItem] = useState<StorageItem | null>(null);

    const download = async (item: StorageItem): Promise<void> => {
        if (!item.projectId || downloadingId) return;

        setDownloadingId(item.id);
        try {
            await downloadScreenshots(
                item.projectId,
                item.projectName === '—' ? item.projectId : item.projectName,
            );
        } catch (error) {
            if (error instanceof DownloadError && error.status === 404) {
                setUnavailable((prev) => new Set(prev).add(item.id));
            }
            toast(error instanceof Error ? error.message : 'Не удалось скачать архив');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownload = (item: StorageItem): void => {
        if (item.projectStorage >= CONFIRM_SIZE_MB) {
            setConfirmItem(item);
            return;
        }
        void download(item);
    };

    /* ─── прочее ─── */

    const changeScope = (next: StorageScope) => {
        setScope(next);
        setPage(1);
        setSearch('');
        setUnavailable(new Set());
    };

    const changeSearch = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleToggle = async (next: boolean) => {
        try {
            await toggleCleanup(next).unwrap();
        } catch {
            toast('При изменении статуса произошла ошибка');
        }
    };

    const emptyState = (() => {
        if (isError) {
            return (
                <EmptyState
                    title="Не удалось загрузить список"
                    description="Проверьте соединение и попробуйте ещё раз."
                    action={{ label: 'Повторить', onClick: () => refetch() }}
                />
            );
        }

        if (items.length === 0) {
            return (
                <EmptyState
                    title="Ничего не найдено"
                    description={search ? 'Проверьте написание или очистите поиск.' : 'Скриншотов в хранилище пока нет.'}
                    action={search ? { label: 'Очистить поиск', onClick: () => changeSearch('') } : undefined}
                />
            );
        }

        return undefined;
    })();

    return (
        <div className={styles.page}>
            <PageHeader
                title="Библиотека скриншотов"
                meta={data ? [<>Всего <b>{formatNumber(total)}</b></>] : undefined}
            />

            <div className={styles.cleanup}>
                {isStatusLoading ? (
                    <span className={styles.muted}>Загружаем настройку…</span>
                ) : (
                    <Switch
                        checked={cleanupEnabled}
                        disabled={isToggling || isStatusError}
                        onChange={handleToggle}
                        label="Автоматическая очистка хранилища спустя 30 дней"
                    />
                )}
                {isStatusError && (
                    <span className={styles.cleanupError}>
                        Временные неполадки в отображении настройки «Автоматическая очистка».
                    </span>
                )}
            </div>

            <Tabs items={TABS} active={scope} onChange={changeScope} />

            <section className={styles.pane} role="tabpanel" aria-label={TABS.find((t) => t.key === scope)?.label}>
                <FilterBar>
                    <FilterSearch value={search} placeholder="Пользователь, организация или проект" onChange={changeSearch} />
                </FilterBar>

                <DataTable
                    className={styles.table}
                    scrollKey={`${scope}-${page}-${query}`}
                    loading={isFetching}
                    isLoading={isLoading}
                    loadingText="Загружаем библиотеку…"
                    empty={emptyState}
                    footer={
                        total > 0 ? (
                            <TableFooter
                                page={page}
                                totalPages={pageCount}
                                rangeLabel={formatRangeLabel(page, DEFAULT_PAGE_SIZE, total)}
                                onPageChange={setPage}
                            />
                        ) : undefined
                    }
                >
                    <colgroup>
                        <col className={styles.colOwner} />
                        <col className={styles.colProject} />
                        <col className={styles.colSize} />
                        <col className={styles.colSize} />
                        <col className={styles.colDate} />
                        <col className={styles.colAction} />
                    </colgroup>

                    <thead>
                        <tr>
                            <th scope="col">{scope === 'users' ? 'Пользователь' : 'Организация'}</th>
                            <th scope="col">Проект</th>
                            <th scope="col" className={styles.center}>Объём проекта</th>
                            <th scope="col" className={styles.center}>
                                {scope === 'users' ? 'Всего у пользователя' : 'Всего у организации'}
                            </th>
                            <th scope="col">Загружено</th>
                            <th scope="col" aria-label="Скачать архив" />
                        </tr>
                    </thead>

                    <tbody>
                        {items.map((item) => {
                            const canDownload = Boolean(item.projectId) && !unavailable.has(item.id);
                            const busy = downloadingId === item.id;

                            return (
                                <tr key={item.id}>
                                    <td className={styles.owner}>{item.title}</td>
                                    <td>
                                        <span className={styles.project} title={item.projectName}>
                                            {item.projectName === '—' ? 'Отсутствует' : item.projectName}
                                        </span>
                                        <small className={styles.mono}>{item.operationId}</small>
                                    </td>
                                    <td className={cx(styles.center, styles.cellNums)}>{formatSize(item.projectStorage)}</td>
                                    <td className={cx(styles.center, styles.cellNums)}>{formatSize(item.totalStorage)}</td>
                                    <td className={styles.cellNums}>{formatDate(item.uploadedAt)}</td>
                                    <td className={styles.action}>
                                        {canDownload && (
                                            <Tooltip
                                                text={busy ? 'Собираем архив…' : `Скачать архив (${formatSize(item.projectStorage)})`}
                                                align="right"
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.download}
                                                    disabled={Boolean(downloadingId)}
                                                    aria-label="Скачать архив скриншотов"
                                                    onClick={() => handleDownload(item)}
                                                >
                                                    {busy ? <IconSpinner className={styles.spinner} /> : <IconDownload />}
                                                </button>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </DataTable>
            </section>

            <ConfirmModal
                open={confirmItem !== null}
                title="Скачать большой архив?"
                description={
                    confirmItem
                        ? `Архив проекта «${confirmItem.projectName}» — ${formatSize(confirmItem.projectStorage)}. Сборка займёт до минуты, страницу в это время лучше не закрывать.`
                        : undefined
                }
                confirmLabel="Скачать"
                onConfirm={() => {
                    const item = confirmItem;
                    setConfirmItem(null);
                    if (item) void download(item);
                }}
                onClose={() => setConfirmItem(null)}
            />
        </div>
    );
}